import {
  connectorGeometry,
  createConnector,
  type ConnectorEndpoint,
  type ConnectorNode,
  type NodeId,
  type Vec,
} from '@kanvas/board-core';
import { invalidate, scratch } from '../../state/scratch';
import { useEditor } from '../../state/store';
import { pickAnchor, pickConnectorTarget } from '../picking';
import type { Gesture, GestureContext } from '../types';

/**
 * Resolves what the loose end of a connector should attach to.
 *
 * Priority is anchor first, then any node under the pointer (auto anchor), then
 * a free point. Preferring an explicit anchor when one is close is what lets a
 * user aim at a specific side of a box.
 */
function resolveTarget(
  ctx: GestureContext,
  exclude: ReadonlySet<NodeId>,
): { endpoint: ConnectorEndpoint; targetId: NodeId | null; point: Vec } {
  const vp = useEditor.getState().viewport;
  const anchor = pickAnchor(ctx.doc, ctx.pointer.board, vp, exclude);
  if (anchor) {
    scratch.anchorHint = anchor;
    return {
      endpoint: {
        kind: 'node',
        nodeId: anchor.nodeId,
        anchor: { kind: 'fixed', nx: anchor.nx, ny: anchor.ny },
      },
      targetId: anchor.nodeId,
      point: anchor.point,
    };
  }
  scratch.anchorHint = null;

  const node = pickConnectorTarget(ctx.doc, ctx.pointer.board, vp, exclude);
  if (node) {
    return {
      endpoint: { kind: 'node', nodeId: node.id, anchor: { kind: 'auto' } },
      targetId: node.id,
      point: ctx.pointer.board,
    };
  }

  return {
    endpoint: { kind: 'point', x: ctx.pointer.board.x, y: ctx.pointer.board.y },
    targetId: null,
    point: ctx.pointer.board,
  };
}

/** Drawing a new connector from a start endpoint. */
export function createConnectorGesture(
  ctx: GestureContext,
  start: ConnectorEndpoint,
  startPoint: Vec,
): Gesture {
  const doc = ctx.doc;
  const style = useEditor.getState().connectorStyle;
  const exclude = new Set<NodeId>(start.kind === 'node' ? [start.nodeId] : []);

  scratch.connectorPreview = {
    from: startPoint,
    to: startPoint,
    routing: style.routing,
    targetId: null,
  };
  scratch.gestureActive = true;

  let end: ConnectorEndpoint = { kind: 'point', x: startPoint.x, y: startPoint.y };
  let moved = false;

  return {
    cursor: 'crosshair',
    move(next) {
      const resolved = resolveTarget(next, exclude);
      end = resolved.endpoint;
      moved = true;
      scratch.connectorPreview = {
        from: startPoint,
        to: resolved.point,
        routing: style.routing,
        targetId: resolved.targetId,
      };
      scratch.highlightIds = resolved.targetId ? [resolved.targetId] : [];
      invalidate();
    },

    up(next) {
      scratch.connectorPreview = null;
      scratch.highlightIds = [];
      scratch.anchorHint = null;
      scratch.gestureActive = false;

      const sameNode =
        start.kind === 'node' && end.kind === 'node' && start.nodeId === end.nodeId;
      const tooShort =
        !moved ||
        (end.kind === 'point' &&
          Math.hypot(end.x - startPoint.x, end.y - startPoint.y) < 8);

      if (sameNode || tooShort) {
        invalidate();
        return;
      }

      const node = createConnector(start, end, {
        userId: next.identity.userId,
        index: doc.topIndex(),
      }, {
        routing: style.routing,
        startHead: style.startHead,
        endHead: style.endHead,
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
        strokeStyle: style.strokeStyle,
      });
      doc.add(node);
      doc.breakUndoBatch();

      const editor = useEditor.getState();
      editor.select([node.id]);
      if (!editor.toolLocked) editor.setTool('select');
      invalidate();
    },

    cancel() {
      scratch.connectorPreview = null;
      scratch.highlightIds = [];
      scratch.anchorHint = null;
      scratch.gestureActive = false;
      invalidate();
    },
  };
}

/** Dragging one end of an existing connector to a new target. */
export function createConnectorEndpointGesture(
  ctx: GestureContext,
  connectorId: NodeId,
  which: 'start' | 'end',
): Gesture {
  const doc = ctx.doc;
  const connector = doc.get(connectorId);
  if (!connector || connector.type !== 'connector') {
    return { move() {}, up() {}, cancel() {} };
  }

  const original: Partial<ConnectorNode> = { [which]: connector[which] };
  const exclude = new Set<NodeId>([connectorId]);
  const other = which === 'start' ? connector.end : connector.start;
  if (other.kind === 'node') exclude.add(other.nodeId);

  let last: Partial<ConnectorNode> | null = null;

  return {
    cursor: 'crosshair',
    move(next) {
      const resolved = resolveTarget(next, exclude);
      last = { [which]: resolved.endpoint };
      scratch.highlightIds = resolved.targetId ? [resolved.targetId] : [];
      scratch.gestureActive = true;
      doc.updateManyEphemeral(new Map([[connectorId, last as never]]));
      invalidate();
    },

    up() {
      scratch.highlightIds = [];
      scratch.anchorHint = null;
      scratch.gestureActive = false;
      if (last) {
        doc.commitGesture(
          new Map([[connectorId, original as never]]),
          new Map([[connectorId, last as never]]),
        );
      }
      invalidate();
    },

    cancel() {
      scratch.highlightIds = [];
      scratch.anchorHint = null;
      scratch.gestureActive = false;
      doc.updateManyEphemeral(new Map([[connectorId, original as never]]));
      invalidate();
    },
  };
}

/**
 * Dragging the midpoint of a connector to bend it.
 *
 * One bend point is enough to cover the cases people actually reach for
 * (routing a line around a box); the geometry code interprets it per routing
 * mode, so the same gesture works for straight, elbow and curved lines.
 */
export function createBendGesture(ctx: GestureContext, connectorId: NodeId): Gesture {
  const doc = ctx.doc;
  const connector = doc.get(connectorId);
  if (!connector || connector.type !== 'connector') {
    return { move() {}, up() {}, cancel() {} };
  }

  const original: Partial<ConnectorNode> = { bend: connector.bend };
  let last: Partial<ConnectorNode> | null = null;

  return {
    cursor: 'move',
    move(next) {
      last = { bend: [{ x: next.pointer.board.x, y: next.pointer.board.y }] };
      scratch.gestureActive = true;
      doc.updateManyEphemeral(new Map([[connectorId, last as never]]));
      invalidate();
    },
    up() {
      scratch.gestureActive = false;
      if (last) {
        doc.commitGesture(
          new Map([[connectorId, original as never]]),
          new Map([[connectorId, last as never]]),
        );
      }
      invalidate();
    },
    cancel() {
      scratch.gestureActive = false;
      doc.updateManyEphemeral(new Map([[connectorId, original as never]]));
      invalidate();
    },
  };
}

/** Screen-space grips shown on a selected connector. */
export interface ConnectorGrips {
  start: Vec;
  end: Vec;
  mid: Vec;
}

export function connectorGrips(
  ctx: { doc: GestureContext['doc'] },
  connector: ConnectorNode,
): ConnectorGrips {
  const geo = connectorGeometry(connector, ctx.doc.lookup);
  return { start: geo.start, end: geo.end, mid: geo.mid };
}
